import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import CRUDFormikField from "./CRUDFormikField";
import { generateYupSchema, getInitialValues } from "../../validation/schemas/yupSchemaGenerator";

export default function CRUDFormikForm({
  formFields,
  initialData,
  onSubmit,
  customHandlers,
  activeTab,
  setActiveTab,
  renderButtons,
  mode,
  transferId,
}) {
  // Filter fields based on mode (e.g. password only shown on add)
  const visibleFields = useMemo(() => {
    return formFields.filter((f) => !f.showOnlyInModes || f.showOnlyInModes.includes(mode));
  }, [formFields, mode]);

  // Generate Yup validation schema from formFields
  const validationSchema = useMemo(() => {
    return generateYupSchema(visibleFields);
  }, [visibleFields]);

  // Get initial values
  const initialValues = useMemo(() => {
    return getInitialValues(visibleFields, initialData);
  }, [visibleFields, initialData]);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      await onSubmit({ preventDefault: () => {}, ...values });
    } catch (error) {
      console.error("Form submission error:", error);
      setErrors({ submit: error.message || "An error occurred" });
    } finally {
      setSubmitting(false);
    }
  };

  // Render form field wrapper
  const renderFormField = (field, formikProps) => {
    return (
      <CRUDFormikField
        key={field.name}
        field={field}
        formikProps={formikProps}
        customHandlers={customHandlers}
      />
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur={true}
      validateOnChange={true}
    >
      {(formikProps) => {
        const { isSubmitting, errors, values, handleSubmit: formikHandleSubmit } = formikProps;

        return (
          <Form className="form_main" onSubmit={formikHandleSubmit}>
            {errors.submit && (
              <div className="mb-4 p-3 border rounded bg-red-100 border-red-400 text-red-700">
                {errors.submit}
              </div>
            )}

            {customHandlers?.renderFormSections ? (
              customHandlers.renderFormSections(
                visibleFields,
                values,
                (e) => {
                  const { name, value } = e.target;
                  formikProps.setFieldValue(name, value);
                },
                mode,
                (field) => renderFormField(field, formikProps),
                activeTab,
                setActiveTab,
                transferId
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                {visibleFields.map((field) => (
                  <div
                    key={field.name}
                    className={field.fullWidth ? "col-span-1 sm:col-span-2" : "col-span-1"}
                  >
                    {renderFormField(field, formikProps)}
                  </div>
                ))}
              </div>
            )}

            {renderButtons && renderButtons(formikProps, isSubmitting)}
          </Form>
        );
      }}
    </Formik>
  );
}
